import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "@/components/Toast";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { ChevronLeft, ChevronRight, X, Plus, Trash2, Pencil, Printer, Repeat, CalendarCheck, ShieldAlert, PartyPopper, ListChecks, BarChart3, History } from "lucide-react";
import { ROLE_LABELS } from "@/lib/roles";
import { SiteSelect } from "@/components/SiteSelect";
import { SiteAssignedHint } from "@/components/SiteAssignedHint";
import { ShiftSelect } from "@/components/ShiftSelect";
import { Time24Select } from "@/components/Time24Select";
import { ScheduleRulesPanel } from "@/components/ScheduleRulesPanel";
import { ScheduleHolidaysPanel } from "@/components/ScheduleHolidaysPanel";
import { ScheduleCoverageView } from "@/components/ScheduleCoverageView";
import { ScheduleAnalyticsView } from "@/components/ScheduleAnalyticsView";
import { ScheduleDeletionsPanel } from "@/components/ScheduleDeletionsPanel";
import { ScheduleRepeatMonth } from "@/components/ScheduleRepeatMonth";
import { useAuth } from "@/contexts/AuthContext";

const WEEKDAYS = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
const WEEKDAY_CODES_CAL = ["dom", "lun", "mar", "mie", "jue", "vie", "sab"];
function shiftAppliesOnDay(shift, dateStr) {
  const wd = new Date(`${dateStr}T00:00:00Z`).getUTCDay();
  const days = shift.days_of_week || [];
  return days.length === 0 || days.includes(WEEKDAY_CODES_CAL[wd]);
}

function pad2(n) { return String(n).padStart(2, "0"); }
function ymd(y, m, d) { return `${y}-${pad2(m)}-${pad2(d)}`; }
function daysInMonth(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }
function firstWeekday(y, m) { return new Date(Date.UTC(y, m - 1, 1)).getUTCDay(); }
function monthLabel(y, m) { return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString("es-AR", { month: "long", year: "numeric", timeZone: "UTC" }); }
function todayAR() { return new Date().toLocaleDateString("en-CA", { timeZone: "America/Argentina/Buenos_Aires" }); }
function weekdayOf(dateStr) { return new Date(`${dateStr}T00:00:00Z`).getUTCDay(); }

const navBtnStyle = { border: "1px solid var(--invict-bone-3)", background: "#fff", borderRadius: 6, padding: 6, cursor: "pointer", color: "#0A0A0A", display: "flex" };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(10,10,10,0.7)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 200 };
const modalStyle = { background: "#fff", borderRadius: 12, padding: 18, width: "100%", maxWidth: 460, maxHeight: "88vh", overflowY: "auto" };
const EMPTY_ADD = { client_id: "", site_id: "", user_id: "", time_from: "", time_to: "", is_day_off: false, day_off_half: "", notes: "", repeat: false, repeat_to: "", weekdays: [], shift_choice: "manual" };

// Fix BP (2026-11-03): medio franco (mañana/tarde), con un color distinto al franco completo.
// Fix BT (2026-11-04): mañana y tarde con colores distintos (pedido del cliente). Mañana sigue en turquesa; tarde
// en violeta, distinto del #7c3aed de "Franco trabajado" y de las guardias pasivas.
const HALF_OFF_COLORS = { manana: "#0891b2", tarde: "#9333ea" };
const halfOffColor = (a) => HALF_OFF_COLORS[a.day_off_half] || HALF_OFF_COLORS.manana;
const dayOffText = (a) => (a.day_off_half === "manana" ? "MEDIO FRANCO · MAÑANA" : a.day_off_half === "tarde" ? "MEDIO FRANCO · TARDE" : "FRANCO");
function DayOffKind({ value, onChange, testId }) {
  return (
    <div className="field"><label>Tipo de franco</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}>
        <option value="">Franco completo</option>
        <option value="manana">Medio franco (mañana)</option>
        <option value="tarde">Medio franco (tarde)</option>
      </select>
    </div>
  );
}

export function ScheduleCalendar() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const today = todayAR();
  const [year, setYear] = useState(Number(today.slice(0, 4)));
  const [month, setMonth] = useState(Number(today.slice(5, 7)));
  const [clients, setClients] = useState([]);
  const [roster, setRoster] = useState([]);
  const [data, setData] = useState({ assignments: [], totals_by_user: [], totals_by_client: [], totals_by_site: [], contract_info: null });
  const [filterClient, setFilterClient] = useState("");
  const [filterSite, setFilterSite] = useState("");
  const [filterSites, setFilterSites] = useState([]);
  const [filterUser, setFilterUser] = useState("");
  const [loading, setLoading] = useState(false);
  const [dayModal, setDayModal] = useState(null);
  const [addForm, setAddForm] = useState(null);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [holidaysOpen, setHolidaysOpen] = useState(false);
  const [holidaysYear, setHolidaysYear] = useState(year);
  const [holidaysMap, setHolidaysMap] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [multiMode, setMultiMode] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  const [bulkForm, setBulkForm] = useState(null);
  const [coverageRefreshKey, setCoverageRefreshKey] = useState(0);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteUserFilter, setBulkDeleteUserFilter] = useState("");
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [deletionsOpen, setDeletionsOpen] = useState(false);
  const [repeatOpen, setRepeatOpen] = useState(false); // Fix BP
  // Fix AQ (2026-09-25): al elegir la persona en "Agregar turno", avisar qué tiene ese día
  // en cualquier objetivo (el cronograma filtrado no lo muestra).
  const [personElsewhere, setPersonElsewhere] = useState([]);
  useEffect(() => {
    const uid = addForm?.user_id;
    if (!uid || !dayModal) { setPersonElsewhere([]); return; }
    let cancel = false;
    api.get("/schedule", { params: { month: dayModal.slice(0, 7), user_id: uid } })
      .then(({ data: d }) => { if (!cancel) setPersonElsewhere((d.assignments || []).filter((x) => x.date === dayModal)); })
      .catch(() => { if (!cancel) setPersonElsewhere([]); });
    return () => { cancel = true; };
  }, [addForm?.user_id, dayModal]);
  // Fix AS (2026-09-25): vacantes (turnos liberados al desactivar o eliminar a alguien).
  const [vacancies, setVacancies] = useState([]);
  const [vacancyPick, setVacancyPick] = useState({});

  const monthStr = `${year}-${pad2(month)}`;

  const loadRefs = async () => {
    try {
      const [c, r] = await Promise.all([api.get("/clients"), api.get("/users/roster")]);
      setClients(c.data || []);
      setRoster(r.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = { month: monthStr };
      if (filterClient) params.client_id = filterClient;
      if (filterSite) params.site_id = filterSite;
      if (filterUser) params.user_id = filterUser;
      const { data: d } = await api.get("/schedule", { params });
      setData(d);
      // Fix AS (2026-09-25): vacantes abiertas del mes con los mismos filtros.
      try {
        const vp = { month: monthStr, status: "abierta" };
        if (filterClient) vp.client_id = filterClient;
        if (filterSite) vp.site_id = filterSite;
        const { data: v } = await api.get("/schedule/vacancies", { params: vp });
        setVacancies(v || []);
      } catch { setVacancies([]); }
      setCoverageRefreshKey((k) => k + 1);
      loadSuggestions();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const loadHolidays = async () => {
    try {
      const params = { year };
      if (filterClient) params.client_id = filterClient;
      if (filterSite) params.site_id = filterSite;
      const { data: h } = await api.get("/schedule/holidays", { params });
      const map = {};
      (h || []).forEach((x) => {
        if (map[x.date]) {
          map[x.date] = { ...map[x.date], name: `${map[x.date].name} + ${x.name}` };
        } else {
          map[x.date] = x;
        }
      });
      setHolidaysMap(map);
    } catch { setHolidaysMap({}); }
  };

  const loadSuggestions = async () => {
    if (!filterClient) { setSuggestions([]); return; }
    try {
      const { data } = await api.get("/schedule/holiday-suggestions", { params: { client_id: filterClient, site_id: filterSite || undefined, month: monthStr } });
      setSuggestions(data.suggestions || []);
    } catch { setSuggestions([]); }
  };

  useEffect(() => { loadRefs(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [monthStr, filterClient, filterSite, filterUser]);
  useEffect(() => { loadHolidays(); setHolidaysYear(year); /* eslint-disable-next-line */ }, [year, filterClient, filterSite]);

  const applySuggestion = (sug) => {
    const entry = data.assignments.find((e) => e.id === sug.assignment_id);
    if (!entry) return;
    startEdit(entry);
    if (sug.suggested_is_day_off) {
      setEditing((prev) => ({ ...prev, is_day_off: true, day_off_reason: "Feriado sin servicio" }));
    } else {
      setEditing((prev) => ({ ...prev, is_day_off: false, shift_choice: "manual", time_from: sug.suggested_time_from, time_to: sug.suggested_time_to }));
    }
  };

  const dismissSuggestion = async (sug) => {
    try {
      await api.post("/schedule/holiday-suggestions/dismiss", { client_id: filterClient, site_id: filterSite || null, holiday_id: sug.holiday_id });
      toast.success("Se dejó el horario original para esta sede/feriado");
      loadSuggestions();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => {
    setFilterSite("");
    if (!filterClient) { setFilterSites([]); return; }
    api.get(`/clients/${filterClient}/sites`).then(({ data: s }) => setFilterSites(s || [])).catch(() => setFilterSites([]));
  }, [filterClient]);

  const changeMonth = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const nDays = daysInMonth(year, month);
  const leadBlank = firstWeekday(year, month);
  const cells = [];
  for (let i = 0; i < leadBlank; i++) cells.push(null);
  for (let d = 1; d <= nDays; d++) cells.push(ymd(year, month, d));

  const entriesForDay = (day) => data.assignments.filter((a) => a.date === day);

  const openDay = (day) => { setDayModal(day); setAddForm(null); setEditing(null); };
  const closeDay = () => { setDayModal(null); setAddForm(null); setEditing(null); };
  const startAdd = () => setAddForm({ ...EMPTY_ADD, client_id: filterClient || "", site_id: filterSite || "", user_id: filterUser || "", weekdays: [weekdayOf(dayModal)] });
  const startEdit = (a) => setEditing({ ...a, repeat: false, shift_choice: "manual", is_day_off: !!a.is_day_off });

  const onDayClick = (day) => {
    if (multiMode) setSelectedDays((prev) => (prev.includes(day) ? prev.filter((x) => x !== day) : [...prev, day]));
    else openDay(day);
  };

  const selectWeekdaysPattern = () => {
    const days = cells.filter((d) => d && weekdayOf(d) >= 1 && weekdayOf(d) <= 5);
    setSelectedDays(days);
  };

  const showOverlaps = (conflicts) => {
    if (conflicts && conflicts.length) toast.info(`Ojo: esta persona ya tiene ${conflicts.length} turno(s) superpuesto(s) ese día.`);
  };
  const showViolations = (violations) => {
    (violations || []).forEach((v) => toast.info(v.message));
  };

  // Fix AS (2026-09-25): cubrir una vacante crea el turno por la vía normal (reglas, auditoría)
  // y reasigna las rondas de ese turno a la persona nueva (endpoint ya publicado en AJ).
  const assignVacancy = async (vac) => {
    const uid = vacancyPick[vac.id];
    if (!uid) { toast.error("Elegí quién cubre la vacante"); return; }
    if (busy) return;
    setBusy(true);
    try {
      const { data: res } = await api.post(`/schedule/vacancies/${vac.id}/assign`, { user_id: uid });
      toast.success("Vacante cubierta");
      showOverlaps(res.overlap_warning);
      showViolations(res.rule_violations);
      setVacancyPick((p) => ({ ...p, [vac.id]: "" }));
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const toggleWeekday = (wd) => setAddForm((f) => ({ ...f, weekdays: f.weekdays.includes(wd) ? f.weekdays.filter((x) => x !== wd) : [...f.weekdays, wd] }));

  const computeRepeatDates = () => {
    const dates = [];
    let cursor = dayModal;
    while (cursor <= addForm.repeat_to) {
      if (addForm.weekdays.includes(weekdayOf(cursor))) dates.push(cursor);
      const d = new Date(`${cursor}T00:00:00Z`); d.setUTCDate(d.getUTCDate() + 1);
      cursor = d.toISOString().slice(0, 10);
    }
    return dates;
  };

  const submitAdd = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true);
    try {
      const base = {
        client_id: addForm.client_id, site_id: addForm.site_id || null, user_id: addForm.user_id,
        is_day_off: addForm.is_day_off, notes: addForm.notes || null,
        ...(addForm.is_day_off ? { day_off_half: addForm.day_off_half || null } : { time_from: addForm.time_from, time_to: addForm.time_to }),
      };
      if (addForm.repeat && addForm.repeat_to) {
        const dates = computeRepeatDates();
        if (dates.length === 0) { toast.error("Elegí al menos un día de la semana dentro del rango"); setBusy(false); return; }
        const { data: res } = await api.post("/schedule/bulk", { ...base, dates });
        toast.success(`${res.created.length} turno(s) creado(s)`);
        const totalOverlaps = Object.values(res.overlaps_by_date || {}).flat().length;
        if (totalOverlaps > 0) toast.info(`Ojo: ${totalOverlaps} de esos turnos quedaron superpuestos con otro de la misma persona.`);
        const blockedN = Object.keys(res.blocked_by_rules || {}).length;
        if (blockedN > 0) toast.error(`${blockedN} día(s) no se asignaron por romper una regla bloqueante.`);
        res.created.forEach((c) => showViolations(c.rule_violations));
      } else {
        const { data: res } = await api.post("/schedule", { ...base, date: dayModal });
        toast.success(addForm.is_day_off ? "Franco asignado" : "Turno asignado");
        showOverlaps(res.overlap_warning);
        showViolations(res.rule_violations);
      }
      setAddForm(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitEdit = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true);
    try {
      const payload = {
        client_id: editing.client_id, site_id: editing.site_id || null, user_id: editing.user_id,
        date: editing.date, is_day_off: editing.is_day_off, notes: editing.notes || null,
        ...(editing.is_day_off ? { day_off_half: editing.day_off_half || null } : { time_from: editing.time_from, time_to: editing.time_to }),
        ...(editing.is_day_off && editing.day_off_reason ? { day_off_reason: editing.day_off_reason } : {}), // Fix AQ
      };
      const { data: res } = await api.patch(`/schedule/${editing.id}`, payload);
      toast.success("Turno actualizado");
      showOverlaps(res.overlap_warning);
      showViolations(res.rule_violations);
      setEditing(null);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submitBulkAssign = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true);
    try {
      const base = {
        client_id: bulkForm.client_id, site_id: bulkForm.site_id || null, user_id: bulkForm.user_id,
        is_day_off: bulkForm.is_day_off, notes: bulkForm.notes || null, dates: selectedDays,
        ...(bulkForm.is_day_off ? { day_off_half: bulkForm.day_off_half || null } : { time_from: bulkForm.time_from, time_to: bulkForm.time_to }),
      };
      const { data: res } = await api.post("/schedule/bulk", base);
      toast.success(`${res.created.length} de ${selectedDays.length} día(s) asignados`);
      const totalOverlaps = Object.values(res.overlaps_by_date || {}).flat().length;
      if (totalOverlaps > 0) toast.info(`Ojo: ${totalOverlaps} turno(s) quedaron superpuestos.`);
      const blockedN = Object.keys(res.blocked_by_rules || {}).length;
      if (blockedN > 0) toast.error(`${blockedN} día(s) NO se asignaron por romper una regla bloqueante.`);
      res.created.forEach((c) => showViolations(c.rule_violations));
      setBulkForm(null); setSelectedDays([]); setMultiMode(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const removeEntry = async (a) => {
    if (!window.confirm(`¿Quitar el ${a.is_day_off ? "franco" : "turno"} de ${a.user_name} de este día?`)) return;
    setBusy(true);
    try { await api.delete(`/schedule/${a.id}`); toast.success("Eliminado"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const printSchedule = async () => {
    if (!filterClient) return toast.error("Elegí un objetivo en el filtro para exportar su cronograma");
    try {
      const params = { client_id: filterClient, month: monthStr };
      if (filterSite) params.site_id = filterSite;
      const res = await api.get("/schedule/print.pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      window.open(url, "_blank");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const contractedInfo = data.contract_info?.shifts?.length ? data.contract_info : null;
  const dayEntries = dayModal ? entriesForDay(dayModal) : [];

  // CAMBIO 4 — preview del borrado masivo (desde datos ya cargados en memoria)
  // Fix AE (2026-09-22): el preview respeta TODOS los filtros visibles de la
  // vista (objetivo, sede y persona) + el limitador de persona del modal -- lo
  // que se muestra en la confirmación es EXACTAMENTE lo que se borra (por ID).
  const bulkPreviewAssignments = selectedDays.length
    ? data.assignments.filter(
        (a) =>
          selectedDays.includes(a.date) &&
          (!filterClient || a.client_id === filterClient) &&
          (!filterSite || a.site_id === filterSite) &&
          (!filterUser || a.user_id === filterUser) &&
          (!bulkDeleteUserFilter || a.user_id === bulkDeleteUserFilter),
      )
    : [];
  const bulkPreviewCoverages = bulkPreviewAssignments.filter((a) => a.is_coverage_override).length;
  const bulkPreviewDayOffs = bulkPreviewAssignments.filter((a) => a.is_day_off).length;

  // CAMBIO 2 (Fix AE): desglose real por sede + persona, y fechas, para que
  // la confirmación diga el alcance exacto que se va a borrar -- no solo el número.
  const bulkPreviewBySite = useMemo(() => {
    const bySite = {};
    for (const a of bulkPreviewAssignments) {
      const siteKey = a.site_name || (a.site_id ? "Sede sin nombre" : "Sin sede asignada");
      if (!bySite[siteKey]) bySite[siteKey] = {};
      bySite[siteKey][a.user_name] = (bySite[siteKey][a.user_name] || 0) + 1;
    }
    return bySite;
  }, [bulkPreviewAssignments]);
  const bulkPreviewDatesLabel = [...new Set(bulkPreviewAssignments.map((a) => a.date))]
    .sort()
    .map((d) => d.slice(8, 10))
    .join(", ");

  const submitBulkDelete = async () => {
    setBulkDeleteBusy(true);
    try {
      // Fix AE (2026-09-22): antes se borraba por filtro (dates + client_id + user_id) y el
      // filtro NO incluía la sede. Con una sede filtrada, se borraban esas fechas en TODAS las
      // sedes del objetivo, mientras la confirmación mostraba solo los de la sede visible.
      // Pasó el 22/09 en Hugo del Carmen Ojeda: se perdieron 10 días en 7 sedes.
      // Ahora se borra por ID exactamente lo que el usuario vio y confirmó.
      const { data: res } = await api.post("/schedule/bulk-delete", { ids: bulkPreviewAssignments.map((x) => x.id) });
      toast.success(`${res.deleted} turno(s) eliminado(s)`);
      if (res.coverage_shifts > 0)
        toast.info(`${res.coverage_shifts} de esos era(n) cobertura. El historial de auditoría se conserva.`);
      if (res.day_offs > 0)
        toast.info(`${res.day_offs} franco(s) eliminado(s) también.`);
      setBulkDeleteOpen(false);
      setBulkDeleteUserFilter("");
      setSelectedDays([]);
      setMultiMode(false);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  return (
    <div data-testid="schedule-calendar">
      <div className="card" style={{ display: "flex", gap: 12, flexWrap: "wrap", padding: 14, marginBottom: 14, alignItems: "flex-end" }}>
        <div className="field" style={{ margin: 0, minWidth: 200 }}>
          <label>Objetivo</label>
          <select value={filterClient} onChange={(e) => setFilterClient(e.target.value)} data-testid="schedule-filter-client">
            <option value="">Todos los objetivos</option>
            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {filterSites.length > 0 && (
          <div className="field" style={{ margin: 0, minWidth: 170 }}>
            <label>Sede</label>
            <select value={filterSite} onChange={(e) => setFilterSite(e.target.value)} data-testid="schedule-filter-site">
              <option value="">Todas las sedes</option>
              {filterSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        )}
        <div className="field" style={{ margin: 0, minWidth: 200 }}>
          <label>Persona</label>
          <select value={filterUser} onChange={(e) => setFilterUser(e.target.value)} data-testid="schedule-filter-user">
            <option value="">Todo el personal</option>
            {roster.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>)}
          </select>
        </div>
        <button className="btn-ghost" onClick={printSchedule} style={{ padding: "9px 14px", fontSize: 11 }} data-testid="schedule-print"><Printer size={13} style={{ marginRight: 6 }} />Exportar / imprimir</button>
        <button className="btn-ghost" onClick={() => setRulesOpen(true)} style={{ padding: "9px 14px", fontSize: 11 }} data-testid="schedule-rules-btn"><ShieldAlert size={13} style={{ marginRight: 6 }} />Reglas de cumplimiento</button>
        <button className="btn-ghost" onClick={() => setHolidaysOpen(true)} style={{ padding: "9px 14px", fontSize: 11 }} data-testid="schedule-holidays-btn"><PartyPopper size={13} style={{ marginRight: 6 }} />Feriados</button>
        <button className={multiMode ? "btn-primary" : "btn-ghost"} onClick={() => { setMultiMode((m) => !m); setSelectedDays([]); }} style={{ padding: "9px 14px", fontSize: 11 }} data-testid="schedule-multi-toggle">
          <ListChecks size={13} style={{ marginRight: 6 }} />{multiMode ? "Salir de selección múltiple" : "Seleccionar varios días"}
        </button>
        <button className="btn-ghost" onClick={() => setDeletionsOpen(true)} style={{ padding: "9px 14px", fontSize: 11 }} data-testid="schedule-deletions-btn">
          <History size={13} style={{ marginRight: 6 }} />Turnos borrados recientemente
        </button>
        {filterClient && (
          <button className="btn-ghost" onClick={() => setRepeatOpen(true)} style={{ padding: "9px 14px", fontSize: 11 }} data-testid="schedule-repeat-btn">
            <Repeat size={13} style={{ marginRight: 6 }} />Repetir en el mes siguiente
          </button>
        )}
      </div>

      {multiMode && (
        <div className="card" style={{ padding: 12, marginBottom: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", background: "#0A0A0A", color: "#FFFBF4" }} data-testid="schedule-multi-bar">
          <span style={{ fontSize: 12 }}>{selectedDays.length} día(s) seleccionado(s)</span>
          <button onClick={selectWeekdaysPattern} className="btn-ghost" style={{ padding: "7px 10px", fontSize: 11 }} data-testid="schedule-multi-pattern-weekdays">Lun a Vie del mes</button>
          {selectedDays.length > 0 && (
            <>
              <button onClick={() => setSelectedDays([])} className="btn-ghost" style={{ padding: "7px 10px", fontSize: 11 }} data-testid="schedule-multi-clear">Limpiar selección</button>
              <button onClick={() => setBulkForm({ ...EMPTY_ADD, client_id: filterClient || "", site_id: filterSite || "", user_id: filterUser || "" })} className="btn-primary" style={{ padding: "7px 12px", fontSize: 11 }} data-testid="schedule-multi-assign-btn">
                Asignar turno a los {selectedDays.length} días
              </button>
              <button onClick={() => { setBulkDeleteUserFilter(""); setBulkDeleteOpen(true); }} className="btn-ghost" style={{ padding: "7px 12px", fontSize: 11, color: "#ef4444", borderColor: "#ef4444" }} data-testid="schedule-multi-delete-btn">
                <Trash2 size={12} style={{ marginRight: 5, display: "inline" }} />Eliminar turnos
              </button>
            </>
          )}
        </div>
      )}

      {contractedInfo && (
        <div className="card" style={{ padding: "12px 14px", marginBottom: 14, background: "#0A0A0A", color: "#FFFBF4" }} data-testid="schedule-contract-compare">
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", marginBottom: contractedInfo.shifts?.length ? 8 : 0 }}>
            <span style={{ fontSize: 12 }}>Contratadas este mes: <b>{contractedInfo.contracted_hours_month} h</b></span>
            <span style={{ fontSize: 12 }}>· Asignadas este mes: <b>{contractedInfo.assigned_hours_month} h</b></span>
            {contractedInfo.contract_is_24h && <span className="badge green">Cobertura 24 hs</span>}
            {contractedInfo.contracted_hours_month > 0 && (
              <span className={`badge ${contractedInfo.assigned_hours_month >= contractedInfo.contracted_hours_month ? "green" : "amber"}`} data-testid="schedule-contract-diff-badge">
                {contractedInfo.assigned_hours_month >= contractedInfo.contracted_hours_month ? "Cubierto" : `Faltan ${Math.round((contractedInfo.contracted_hours_month - contractedInfo.assigned_hours_month) * 100) / 100} h`}
              </span>
            )}
          </div>
          {contractedInfo.shifts?.length > 0 && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, fontSize: 11, opacity: 0.85 }}>
              {contractedInfo.shifts.map((s) => (
                <span key={s.id}>{s.label || "Turno"} {s.time_from}–{s.time_to} x{s.staff_count}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fix AS (2026-09-25): vacantes abiertas del mes; tocar una abre ese día. */}
      {vacancies.length > 0 && (
        <div className="card" style={{ padding: "12px 14px", marginBottom: 12, borderLeft: "4px solid #b45309" }} data-testid="schedule-vacancies-banner">
          <div style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A", marginBottom: 6 }}>Vacantes abiertas este mes: {vacancies.length}</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {vacancies.map((v) => (
              <button key={v.id} type="button" onClick={() => openDay(v.date)} data-testid={`schedule-vacancy-chip-${v.id}`}
                style={{ fontSize: 11, border: "1px solid #b45309", background: "#fff7ed", color: "#92400e", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}>
                {v.date.slice(8, 10)}/{v.date.slice(5, 7)} {v.time_from}–{v.time_to} · {v.client_name}{v.site_name ? ` · ${v.site_name}` : ""} · antes {v.previous_user_name}
              </button>
            ))}
          </div>
        </div>
      )}
      <div className="card" style={{ padding: 16, marginBottom: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
          <button onClick={() => changeMonth(-1)} style={navBtnStyle} data-testid="schedule-prev-month"><ChevronLeft size={18} /></button>
          <div style={{ fontSize: 15, fontWeight: 900, textTransform: "capitalize", color: "#0A0A0A" }} data-testid="schedule-month-label">{monthLabel(year, month)}</div>
          <button onClick={() => changeMonth(1)} style={navBtnStyle} data-testid="schedule-next-month"><ChevronRight size={18} /></button>
        </div>
        <div style={{ fontSize: 10, color: "var(--invict-text-muted)", margin: "6px 0 10px" }}>
          {multiMode ? "Tocá los días que querés seleccionar." : "Tocá un día para asignar, editar o quitar turnos de servicio."}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 4 }}>
          {WEEKDAYS.map((w) => <div key={w} style={{ fontSize: 10, fontWeight: 800, color: "var(--invict-text-muted)", textAlign: "center", padding: "4px 0" }}>{w}</div>)}
          {cells.map((day, i) => {
            if (!day) return <div key={`b${i}`} />;
            const entries = entriesForDay(day);
            const isToday = day === today;
            const holiday = holidaysMap[day];
            const isSelected = selectedDays.includes(day);
            const hasBlockViolation = entries.some((e) => (e.rule_violations || []).some((v) => v.mode === "block"));
            const hasAlertViolation = !hasBlockViolation && entries.some((e) => (e.rule_violations || []).length > 0);
            const hasViolation = hasBlockViolation || hasAlertViolation;
            const dayOvertime = data.overtime_by_date && data.overtime_by_date[day];
            const hasSuggestion = suggestions.some((s) => s.date === day);

            // Fondo de celda: violación > seleccionado > normal
            let cellBg = "#fff";
            if (isSelected) cellBg = "#2a2a2a";
            else if (entries.length && holiday) cellBg = "#1a1500";
            else if (entries.length) cellBg = "#0A0A0A";
            else if (holiday) cellBg = "#fdf8e6";
            if (hasAlertViolation) cellBg = "#1e1400";
            if (hasBlockViolation) cellBg = "#220a0a";

            // Borde: violación > hoy > feriado > default
            let cellBorder = holiday
              ? "2px solid #d4af37"
              : isToday
                ? "2px solid var(--invict-red)"
                : isSelected
                  ? "2px solid var(--invict-red)"
                  : "1px solid var(--invict-bone-3)";
            if (hasAlertViolation) cellBorder = "2px solid #f59e0b";
            if (hasBlockViolation) cellBorder = "2px solid #ef4444";

            const darkCell = isSelected || entries.length > 0 || hasViolation;
            return (
              <button key={day} onClick={() => onDayClick(day)} data-testid={`schedule-day-${day}`} title={holiday ? holiday.name : undefined}
                style={{
                  minHeight: 62, borderRadius: 6, position: "relative",
                  border: cellBorder,
                  background: cellBg, padding: 4, cursor: "pointer",
                  display: "flex", flexDirection: "column", gap: 2, textAlign: "left",
                }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: darkCell ? "#FFFBF4" : "#0A0A0A" }}>{Number(day.slice(8, 10))}</span>
                {dayOvertime && (
                  <span data-testid={`schedule-overtime-day-${day}`} title={`Horas extra: ${dayOvertime.overtime_hours} h (${dayOvertime.user_names.join(", ")})`} style={{
                    position: "absolute", top: 3, left: 3, width: 8, height: 8, borderRadius: "50%", background: "#f59e0b",
                  }} />
                )}
                {hasSuggestion && (
                  <span data-testid={`schedule-suggestion-dot-${day}`} title="Sugerencia de horario de feriado pendiente" style={{
                    position: "absolute", top: 3, right: 3, width: 8, height: 8, borderRadius: "50%", background: "#ef4444",
                  }} />
                )}
                {vacancies.some((v) => v.date === day) && (
                  <span data-testid={`schedule-vacancy-day-${day}`} style={{ fontSize: 8, fontWeight: 900, color: "#fff", background: "#b45309", borderRadius: 8, padding: "1px 5px", alignSelf: "flex-start" }}>
                    VACANTE{vacancies.filter((v) => v.date === day).length > 1 ? ` ×${vacancies.filter((v) => v.date === day).length}` : ""}
                  </span>
                )}
                {holiday && (
                  <span data-testid={`schedule-holiday-${day}`} style={{
                    fontSize: 8, fontWeight: 900, letterSpacing: "0.03em",
                    color: darkCell ? "#f8d95a" : "#a67c00",
                    textTransform: "uppercase",
                  }}>
                    {holiday.name.length > 10 ? holiday.name.slice(0, 9) + "…" : holiday.name}
                  </span>
                )}
                <div style={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                  {entries.slice(0, 3).map((a) => (
                    <span key={a.id} style={{
                      fontSize: 8, fontWeight: 800, color: "#fff", borderRadius: 8, padding: "1px 5px",
                      background: a.is_coverage_override ? "#BF3437" : a.is_day_off ? (a.day_off_half ? halfOffColor(a) : "#4B5563") : "var(--invict-red)",
                      border: a.day_off_worked ? "1px solid #d8b4fe" : "none",
                    }}>
                      {a.is_coverage_override ? "COB" : a.is_day_off ? (a.day_off_half ? "½ FRANCO" : "FRANCO") : (a.user_name || "?").split(" ")[0].slice(0, 6)}
                    </span>
                  ))}
                  {entries.length > 3 && <span style={{ fontSize: 8, color: "rgba(255,251,244,0.7)" }}>+{entries.length - 3}</span>}
                </div>
                {hasBlockViolation && (
                  <span data-testid={`schedule-violation-marker-${day}`} style={{ position: "absolute", top: 3, right: 3, background: "#ef4444", color: "#fff", borderRadius: 10, padding: "1px 5px", fontSize: 10, fontWeight: 900, lineHeight: 1.5 }}>!!</span>
                )}
                {hasAlertViolation && (
                  <span data-testid={`schedule-violation-marker-${day}`} style={{ position: "absolute", top: 3, right: 3, background: "#f59e0b", color: "#000", borderRadius: 10, padding: "1px 5px", fontSize: 10, fontWeight: 900, lineHeight: 1.5 }}>!</span>
                )}
              </button>
            );
          })}
        </div>
        {loading && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 10 }}>Cargando...</div>}
        <div style={{ display: "flex", flexWrap: "wrap", gap: 12, marginTop: 12, fontSize: 10, color: "var(--invict-text-muted)" }} data-testid="schedule-legend">
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "var(--invict-red)", marginRight: 4 }} />Turno normal</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "#4B5563", marginRight: 4 }} />Franco</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: HALF_OFF_COLORS.manana, marginRight: 4 }} />Medio franco (mañana)</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: HALF_OFF_COLORS.tarde, marginRight: 4 }} />Medio franco (tarde)</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "#BF3437", marginRight: 4 }} />Cobertura (turno modificado)</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "#fff", border: "2px solid #7c3aed", marginRight: 4 }} />Franco trabajado (queda adeudado)</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "#fdf8e6", border: "1px solid #d4af37", marginRight: 4 }} />Feriado</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "#1e1400", border: "1px solid #f59e0b", marginRight: 4 }} />Alerta de cumplimiento</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 3, background: "#220a0a", border: "1px solid #ef4444", marginRight: 4 }} />Incumplimiento bloqueante</span>
        </div>
      </div>

      <div className="card" style={{ padding: "12px 16px", marginBottom: 14, background: "#0A0A0A", color: "#FFFBF4", display: "flex", flexWrap: "wrap", gap: 18, alignItems: "center", justifyContent: "space-between" }} data-testid="schedule-total-general">
        <div>
          <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,251,244,0.6)" }}>Total general de horas (este mes)</span>
          <div style={{ fontSize: 18, fontWeight: 800 }}>{data.total_hours_general ?? 0} h</div>
        </div>
        <div>
          <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,251,244,0.6)" }}>Horas extra (este mes)</span>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b" }} data-testid="schedule-overtime-general">{data.overtime_hours_general ?? 0} h</div>
        </div>
        <div>
          <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,251,244,0.6)" }}>Total (base + extra)</span>
          <div style={{ fontSize: 22, fontWeight: 900 }} data-testid="schedule-total-with-overtime">{data.total_hours_general_with_overtime ?? data.total_hours_general ?? 0} h</div>
        </div>
        <div>
          <span style={{ fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase", color: "rgba(255,251,244,0.6)" }}>Horas en feriado (este mes)</span>
          <div style={{ fontSize: 18, fontWeight: 800, color: "#d4af37" }} data-testid="schedule-holiday-hours-general">{data.holiday_hours_general ?? 0} h</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }} className="max-md:!grid-cols-1">
        <div className="card" style={{ padding: 16 }} data-testid="schedule-totals-user">
          <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}>Horas por persona (este mes)</h3>
          {data.totals_by_user.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin turnos cargados.</div>}
          {data.totals_by_user.map((t) => (
            <div key={t.user_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 13 }} data-testid={`schedule-total-user-${t.user_id}`}>
              <span>{t.user_name}{t.day_off_count > 0 && <span className="badge gray" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`schedule-dayoff-count-${t.user_id}`}>{String(t.day_off_count).replace(".", ",")} franco{t.day_off_count > 1 ? "s" : ""}</span>}{t.day_off_worked_count > 0 && <span className="badge purple" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`schedule-dayoffworked-count-${t.user_id}`}>{t.day_off_worked_count} franco{t.day_off_worked_count > 1 ? "s" : ""} trabajado{t.day_off_worked_count > 1 ? "s" : ""}</span>}{t.overtime_hours > 0 && <span className="badge amber" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`schedule-overtime-user-${t.user_id}`}>+{t.overtime_hours}h extra</span>}{t.holiday_hours > 0 && <span className="badge amber" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`schedule-holiday-hours-user-${t.user_id}`}>{t.holiday_hours}h feriado</span>}</span>
              <b>{t.total_with_overtime ?? t.total_hours} h</b>
            </div>
          ))}
        </div>
        <div className="card" style={{ padding: 16 }} data-testid="schedule-totals-client">
          <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}>Horas por objetivo (este mes)</h3>
          {data.totals_by_client.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin turnos cargados.</div>}
          {data.totals_by_client.map((t) => (
            <div key={t.client_id} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 13 }} data-testid={`schedule-total-client-${t.client_id}`}>
              <span>{t.client_name}{t.overtime_hours > 0 && <span className="badge amber" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`schedule-overtime-client-${t.client_id}`}>+{t.overtime_hours}h extra</span>}{t.holiday_hours > 0 && <span className="badge amber" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`schedule-holiday-hours-client-${t.client_id}`}>{t.holiday_hours}h feriado</span>}</span>
              <b>{t.total_with_overtime ?? t.total_hours} h</b>
            </div>
          ))}
        </div>
        <div className="card" style={{ padding: 16 }} data-testid="schedule-totals-site">
          <h3 style={{ margin: "0 0 10px", fontSize: 12, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 800, color: "#0A0A0A" }}>Horas por sede (este mes)</h3>
          {data.totals_by_site.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)" }}>Sin turnos con sede cargados.</div>}
          {data.totals_by_site.map((t) => (
            <div key={t.site_id || t.site_name} style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", borderBottom: "1px solid var(--invict-bone-3)", fontSize: 13 }} data-testid={`schedule-total-site-${t.site_id || "sin-sede"}`}>
              <span>{t.site_name || "Sede"}{t.no_site && <span style={{ color: "var(--invict-text-muted)", fontSize: 11 }}> ({t.client_name})</span>}{t.overtime_hours > 0 && <span className="badge amber" style={{ marginLeft: 6, fontSize: 9 }}>+{t.overtime_hours}h extra</span>}{t.holiday_hours > 0 && <span className="badge amber" style={{ marginLeft: 6, fontSize: 9 }} data-testid={`schedule-holiday-hours-site-${t.site_id || "sin-sede"}`}>{t.holiday_hours}h feriado</span>}</span>
              <b>{t.total_with_overtime ?? t.total_hours} h</b>
            </div>
          ))}
        </div>
      </div>

      <div style={{ margin: "14px 0 4px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <BarChart3 size={14} />
          <h3 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: "#0A0A0A" }}>Analíticas de horas</h3>
        </div>
        <button onClick={() => setShowAnalytics((s) => !s)} className={showAnalytics ? "btn-primary" : "btn-ghost"} style={{ padding: "7px 12px", fontSize: 11 }} data-testid="schedule-analytics-tab">
          {showAnalytics ? "Ocultar analíticas" : "Ver analíticas por objetivo/año →"}
        </button>
      </div>
      {showAnalytics && <ScheduleAnalyticsView clients={clients} defaultClientId={filterClient} />}

      <div style={{ margin: "18px 0 4px", display: "flex", alignItems: "center", gap: 8 }}>
        <CalendarCheck size={14} />
        <h3 style={{ margin: 0, fontSize: 13, fontWeight: 900, color: "#0A0A0A" }}>Análisis de cobertura</h3>
      </div>
      <ScheduleCoverageView clientId={filterClient} siteId={filterSite} month={monthStr} refreshKey={coverageRefreshKey} />

      {dayModal && (
        <div style={overlayStyle} onClick={closeDay} data-testid="schedule-day-modal-overlay">
          <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="schedule-day-modal">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#0A0A0A" }} data-testid="schedule-day-modal-date">{dayModal}</div>
              <button onClick={closeDay} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0A0A0A" }} data-testid="schedule-day-modal-close"><X size={18} /></button>
            </div>

            {/* CAMBIO 3 — Banner de feriado */}
            {holidaysMap[dayModal] && (() => {
              const hol = holidaysMap[dayModal];
              const applicableShifts = (data.contract_info?.shifts || []).filter((s) => shiftAppliesOnDay(s, dayModal));
              const requiredStaff = applicableShifts.reduce((sum, s) => sum + (s.staff_count || 1), 0);
              const assignedCount = dayEntries.filter((e) => !e.is_day_off).length;
              const uncovered = filterClient && requiredStaff > 0 && assignedCount < requiredStaff;
              return (
                <div data-testid={`schedule-holiday-banner-${dayModal}`} style={{ background: "rgba(212,175,55,0.12)", border: "1.5px solid #d4af37", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: "#92700a" }}>
                    <PartyPopper size={13} style={{ display: "inline", marginRight: 5, color: "#d4af37", verticalAlign: "text-bottom" }} />
                    Feriado: {hol.name}
                    <span style={{ fontSize: 10, fontWeight: 500, marginLeft: 8, color: "#a07c14", textTransform: "lowercase" }}>{hol.kind === "nacional" ? "nacional" : hol.kind === "provincial_mendoza" ? "prov. Mendoza" : hol.kind === "sectorial" ? "por rubro" : hol.kind === "cliente" ? "por cliente" : "empresa"}</span>
                  </div>
                  <div style={{ fontSize: 12, color: "#a07c14", marginTop: 4 }} data-testid={`schedule-holiday-config-${dayModal}`}>
                    {/* Fix AS (2026-09-25): en vez del texto fijo, lo configurado en el objetivo/sede. */}
                    {!filterClient
                      ? "Filtrá por objetivo para ver su horario de feriado."
                      : data.contract_info?.no_service_on_holidays
                        ? "Este objetivo no tiene servicio en feriados."
                        : (data.contract_info?.shifts || []).some((s) => (s.days_of_week || []).includes("feriado"))
                          ? `Horario de feriado configurado: ${(data.contract_info.shifts || []).filter((s) => (s.days_of_week || []).includes("feriado")).map((s) => `${s.time_from}–${s.time_to}`).join(", ")}.`
                          : "Sin horario especial de feriado: se cubre con el horario normal. Se configura en la ficha del objetivo."}
                  </div>
                  {uncovered && (
                    <div style={{ fontSize: 12, color: "#ef4444", fontWeight: 700, marginTop: 4 }}>
                      Hay {requiredStaff - assignedCount} persona(s) contratada(s) para este día sin turno asignado.
                    </div>
                  )}
                  {filterClient && requiredStaff === 0 && (
                    <div style={{ fontSize: 11, color: "#a07c14", marginTop: 3 }}>Sin franjas contratadas para este día de la semana.</div>
                  )}
                </div>
              );
            })()}

            {/* Prompt AI - Cambio 3: sugerencias de horario especial en feriados */}
            {suggestions.filter((s) => s.date === dayModal).length > 0 && (
              <div data-testid={`schedule-suggestions-${dayModal}`} style={{ background: "rgba(239,68,68,0.06)", border: "1.5px solid #ef4444", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                <div style={{ fontWeight: 800, fontSize: 12, color: "#b91c1c", marginBottom: 6 }}>
                  Horario de feriado configurado distinto al asignado
                </div>
                {suggestions.filter((s) => s.date === dayModal).map((sug) => (
                  <div key={sug.assignment_id} data-testid={`schedule-suggestion-${sug.assignment_id}`} style={{ padding: "6px 0", borderTop: "1px solid rgba(239,68,68,0.18)" }}>
                    <div style={{ fontSize: 12 }}>
                      <b>{sug.user_name}</b>: actual {sug.current_time_from}–{sug.current_time_to} → sugerido {sug.suggested_is_day_off ? "franco (sin servicio en feriados)" : `${sug.suggested_time_from}–${sug.suggested_time_to}`}
                    </div>
                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <button type="button" onClick={() => applySuggestion(sug)} className="btn-primary" style={{ padding: "4px 10px", fontSize: 11 }} data-testid={`schedule-suggestion-apply-${sug.assignment_id}`}>Aplicar horario de feriado</button>
                      <button type="button" onClick={() => dismissSuggestion(sug)} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "4px 10px", fontSize: 11, cursor: "pointer" }} data-testid={`schedule-suggestion-dismiss-${sug.assignment_id}`}>Dejar el original</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Fix AS (2026-09-25): vacantes del día, para asignar a otra persona. */}
            {vacancies.filter((v) => v.date === dayModal).map((v) => (
              <div key={v.id} data-testid={`schedule-vacancy-${v.id}`} style={{ border: "1.5px dashed #b45309", background: "#fff7ed", borderRadius: 8, padding: 10, marginBottom: 8 }}>
                <div style={{ fontWeight: 800, fontSize: 13, color: "#92400e" }}>VACANTE {v.time_from}–{v.time_to} · antes: {v.previous_user_name}</div>
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 2 }}>{v.client_name}{v.site_name ? ` · ${v.site_name}` : ""}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <select value={vacancyPick[v.id] || ""} onChange={(e) => setVacancyPick((p) => ({ ...p, [v.id]: e.target.value }))} style={{ flex: 1 }} data-testid={`schedule-vacancy-user-${v.id}`}>
                    <option value="">Elegí quién cubre...</option>
                    {roster.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>)}
                  </select>
                  <button type="button" disabled={busy} onClick={() => assignVacancy(v)} className="btn-primary" style={{ padding: "6px 12px", fontSize: 11 }} data-testid={`schedule-vacancy-assign-${v.id}`}>Asignar</button>
                </div>
              </div>
            ))}
            {dayEntries.length === 0 && <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 12 }} data-testid="schedule-day-modal-empty">Sin turnos asignados este día.</div>}

            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 12 }}>
              {dayEntries.map((a) => (
                <div key={a.id} style={{ border: "1px solid var(--invict-bone-3)", borderRadius: 8, padding: 10 }} data-testid={`schedule-entry-${a.id}`}>
                  {editing?.id === a.id ? (
                    <form onSubmit={submitEdit} style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid={`schedule-edit-form-${a.id}`}>
                      <div className="field"><label>Objetivo</label>
                        <select required value={editing.client_id} onChange={(e) => setEditing({ ...editing, client_id: e.target.value, site_id: "", shift_choice: "manual" })}>
                          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </div>
                      <SiteSelect clientId={editing.client_id} value={editing.site_id} onChange={(v) => setEditing({ ...editing, site_id: v, shift_choice: "manual" })} testIdPrefix="schedule-edit"/>
                      <SiteAssignedHint clientId={editing.client_id} siteId={editing.site_id} roster={roster}/>
                      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                        <input type="checkbox" checked={editing.is_day_off} onChange={(e) => setEditing({ ...editing, is_day_off: e.target.checked })} data-testid={`schedule-edit-dayoff-${a.id}`}/>
                        Franco (sin horario, no suma horas)
                      </label>
                      {editing.is_day_off && (
                        <DayOffKind value={editing.day_off_half} onChange={(v) => setEditing({ ...editing, day_off_half: v })} testId={`schedule-edit-dayoff-half-${a.id}`} />
                      )}
                      {!editing.is_day_off && (
                        <>
                          <ShiftSelect clientId={editing.client_id} siteId={editing.site_id} clients={clients} value={editing.shift_choice}
                            onSelectShift={(s) => setEditing((f) => (s
                              ? { ...f, shift_choice: s.id, time_from: s.time_from, time_to: s.time_to }
                              : { ...f, shift_choice: "manual" }))}
                            testIdPrefix={`schedule-edit-${a.id}`}/>
                          <div className="field"><label>Persona</label>
                            <select required value={editing.user_id} onChange={(e) => setEditing({ ...editing, user_id: e.target.value })}>
                              {roster.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>)}
                            </select>
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                            <div className="field"><label>Fecha (mover)</label><input type="date" required value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} data-testid={`schedule-edit-date-${a.id}`}/></div>
                            <div className="field"><label>Desde</label><Time24Select required disabled={editing.shift_choice !== "manual"} value={editing.time_from || ""} onChange={(v) => setEditing({ ...editing, time_from: v })} testId={`schedule-edit-time-from-${a.id}`} style={editing.shift_choice !== "manual" ? { background: "var(--invict-bone-2)" } : undefined}/></div>
                            <div className="field"><label>Hasta</label><Time24Select required disabled={editing.shift_choice !== "manual"} value={editing.time_to || ""} onChange={(v) => setEditing({ ...editing, time_to: v })} testId={`schedule-edit-time-to-${a.id}`} style={editing.shift_choice !== "manual" ? { background: "var(--invict-bone-2)" } : undefined}/></div>
                          </div>
                        </>
                      )}
                      {editing.is_day_off && (
                        <div className="field"><label>Persona</label>
                          <select required value={editing.user_id} onChange={(e) => setEditing({ ...editing, user_id: e.target.value })}>
                            {roster.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>)}
                          </select>
                        </div>
                      )}
                      {/* Fix AQ (2026-09-25): el motivo queda registrado junto al horario original. */}
                      {editing.is_day_off && !a.is_day_off && (
                        <div className="field"><label>Motivo del franco * (queda registrado con el horario original)</label>
                          <input required minLength={3} value={editing.day_off_reason || ""} onChange={(e) => setEditing({ ...editing, day_off_reason: e.target.value })} placeholder="Ej: suspensión de clases" data-testid={`schedule-edit-dayoff-reason-${a.id}`}/>
                        </div>
                      )}
                      <div className="field"><label>Notas</label><input value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })}/></div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button type="submit" disabled={busy} className="btn-primary" style={{ flex: 1, padding: 8 }} data-testid={`schedule-edit-submit-${a.id}`}>{busy ? "Guardando..." : "Guardar cambios"}</button>
                        <button type="button" onClick={() => setEditing(null)} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "0 14px", cursor: "pointer" }}>Cancelar</button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 800, fontSize: 13, color: "#0A0A0A" }}>{a.user_name}</span>
                        {a.user_deleted && <DeletedUserBadge id={a.id} />}
                        <span style={{ fontSize: 10, color: "var(--invict-text-muted)", fontWeight: 700 }}>{ROLE_LABELS[a.user_role] || a.user_role}</span>
                        {a.is_day_off && (a.day_off_half
                          ? <span className="badge" style={{ background: halfOffColor(a), color: "#fff" }} data-testid={`schedule-half-dayoff-${a.id}`}>{dayOffText(a)}</span>
                          : <span className="badge gray">FRANCO</span>)}
                        {a.is_coverage_override && <span className="badge red" data-testid={`schedule-coverage-badge-${a.id}`}>Cobertura</span>}
                        {a.day_off_worked && <span className="badge purple" data-testid={`schedule-dayoffworked-${a.id}`}>Franco trabajado</span>}
                      </div>
                      <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 2 }}>{a.client_name}{a.site_name ? ` · ${a.site_name}` : ""}</div>
                      {!a.is_day_off && <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{a.time_from}–{a.time_to} <span style={{ color: "var(--invict-text-muted)", fontWeight: 400 }}>({a.hours ?? "—"} h)</span></div>}
                      {a.is_day_off && a.day_off_original?.time_from && (
                        <div style={{ fontSize: 11, color: "#0A0A0A", marginTop: 2 }} data-testid={`schedule-dayoff-record-${a.id}`}>
                          Franco en lugar de {a.day_off_original.time_from}–{a.day_off_original.time_to}
                          {a.day_off_reason ? ` · Motivo: ${a.day_off_reason}` : ""}
                          {a.day_off_marked_by ? ` · ${a.day_off_marked_by}` : ""}
                          {a.day_off_marked_at ? ` el ${new Date(a.day_off_marked_at).toLocaleString("es-AR")}` : ""}
                        </div>
                      )}
                      {a.is_coverage_override && a.original_user_name && (
                        <div style={{ fontSize: 11, color: "#BF3437", marginTop: 2 }}>Reemplazó a {a.original_user_name}</div>
                      )}
                      {a.day_off_worked && a.day_off_worked_from_client_name && (
                        <div style={{ fontSize: 11, color: "#7c3aed", marginTop: 2 }} data-testid={`schedule-dayoffworked-debt-${a.id}`}>
                          Tenía franco en {a.day_off_worked_from_client_name} · queda como franco adeudado (ver Horas Extra → Francos adeudados)
                        </div>
                      )}
                      {(a.is_coverage_override || a.day_off_worked) && a.coverage_changed_by_name && (
                        <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginTop: 2 }} data-testid={`schedule-coverage-author-${a.id}`}>
                          Modificado por <b>{a.coverage_changed_by_name}</b>{a.coverage_changed_at ? ` el ${new Date(a.coverage_changed_at).toLocaleString("es-AR")}` : ""}
                          {a.coverage_reason && <><br />Motivo: "{a.coverage_reason}"</>}
                        </div>
                      )}
                      {a.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic", marginTop: 2 }}>{a.notes}</div>}
                      {a.rule_violations?.length > 0 && (
                        <div data-testid={`schedule-violations-block-${a.id}`} style={{
                          marginTop: 8, borderRadius: 6, overflow: "hidden",
                          border: `1px solid ${a.rule_violations.some((v) => v.mode === "block") ? "#ef4444" : "#f59e0b"}`,
                        }}>
                          <div style={{
                            padding: "5px 10px", fontSize: 11, fontWeight: 800,
                            background: a.rule_violations.some((v) => v.mode === "block") ? "rgba(239,68,68,0.12)" : "rgba(245,158,11,0.12)",
                            color: a.rule_violations.some((v) => v.mode === "block") ? "#b91c1c" : "#92400e",
                            display: "flex", alignItems: "center", gap: 6,
                          }}>
                            <ShieldAlert size={13} />
                            {a.rule_violations.some((v) => v.mode === "block") ? "Incumplimiento bloqueante" : "Alerta de cumplimiento"}
                          </div>
                          {a.rule_violations.map((v, idx) => (
                            <div key={idx} data-testid={`schedule-violation-${a.id}-${idx}`} style={{
                              padding: "5px 10px", fontSize: 12,
                              color: v.mode === "block" ? "#b91c1c" : "#92400e",
                              borderTop: idx > 0 ? "1px solid rgba(0,0,0,0.07)" : undefined,
                            }}>
                              <b>{v.mode === "block" ? "Bloqueo" : "Alerta"}:</b> {v.message}
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 14, marginTop: 8 }}>
                        <button onClick={() => startEdit(a)} style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: "#0A0A0A", fontSize: 11, fontWeight: 700, cursor: "pointer" }} data-testid={`schedule-edit-open-${a.id}`}><Pencil size={12} /> Editar / mover</button>
                        {!a.is_day_off && (
                          <button onClick={() => { startEdit(a); setEditing((p) => ({ ...p, is_day_off: true })); }} style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: "#0A0A0A", fontSize: 11, fontWeight: 700, cursor: "pointer" }} data-testid={`schedule-to-dayoff-${a.id}`}>Pasar a franco</button>
                        )}
                        <button onClick={() => removeEntry(a)} style={{ display: "inline-flex", alignItems: "center", gap: 4, border: "none", background: "transparent", color: "var(--invict-red)", fontSize: 11, fontWeight: 700, cursor: "pointer" }} data-testid={`schedule-delete-${a.id}`}><Trash2 size={12} /> Quitar</button>
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>

            {/* Fix AQ (2026-09-25): personal de este objetivo/sede que ese día trabaja en otro lado. */}
            {(() => {
              const others = (data.other_assignments || []).filter((o) => o.date === dayModal);
              if (!others.length) return null;
              return (
                <div data-testid={`schedule-others-${dayModal}`} style={{ background: "rgba(10,10,10,0.04)", border: "1px solid var(--invict-bone-3)", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                  <div style={{ fontWeight: 800, fontSize: 12, color: "#0A0A0A", marginBottom: 6 }}>Este día también trabajan en otro lado</div>
                  {others.map((o) => (
                    <div key={o.id} style={{ fontSize: 12, padding: "3px 0" }} data-testid={`schedule-other-${o.id}`}>
                      <b>{o.user_name}</b>: {o.client_name}{o.site_name ? ` · ${o.site_name}` : ""} — {o.is_day_off ? "FRANCO" : `${o.time_from}–${o.time_to}`}
                    </div>
                  ))}
                </div>
              );
            })()}

            {addForm ? (
              <form onSubmit={submitAdd} style={{ borderTop: "1px solid var(--invict-bone-3)", paddingTop: 12 }} data-testid="schedule-add-form">
                <div className="field"><label>Objetivo *</label>
                  <select required value={addForm.client_id} onChange={(e) => setAddForm({ ...addForm, client_id: e.target.value, site_id: "", shift_choice: "manual" })} data-testid="schedule-add-client">
                    <option value="">Elegir...</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
                <SiteSelect clientId={addForm.client_id} value={addForm.site_id} onChange={(v) => setAddForm({ ...addForm, site_id: v, shift_choice: "manual" })} testIdPrefix="schedule-add"/>
                <SiteAssignedHint clientId={addForm.client_id} siteId={addForm.site_id} roster={roster}/>
                <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", margin: "6px 0" }}>
                  <input type="checkbox" checked={addForm.is_day_off} onChange={(e) => setAddForm({ ...addForm, is_day_off: e.target.checked })} data-testid="schedule-add-dayoff-toggle"/>
                  Franco (sin horario, no suma horas)
                </label>
                {addForm.is_day_off && (
                  <DayOffKind value={addForm.day_off_half} onChange={(v) => setAddForm({ ...addForm, day_off_half: v })} testId="schedule-add-dayoff-half" />
                )}
                {!addForm.is_day_off && (
                  <ShiftSelect clientId={addForm.client_id} siteId={addForm.site_id} clients={clients} value={addForm.shift_choice}
                    onSelectShift={(s) => setAddForm((f) => (s
                      ? { ...f, shift_choice: s.id, time_from: s.time_from, time_to: s.time_to }
                      : { ...f, shift_choice: "manual" }))}
                    testIdPrefix="schedule-add"/>
                )}
                <div className="field"><label>Persona *</label>
                  <select required value={addForm.user_id} onChange={(e) => setAddForm({ ...addForm, user_id: e.target.value })} data-testid="schedule-add-user">
                    <option value="">Elegir...</option>
                    {roster.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>)}
                  </select>
                </div>
                {personElsewhere.length > 0 && (
                  <div data-testid="schedule-add-person-elsewhere" style={{ fontSize: 11, background: "rgba(245,158,11,0.10)", border: "1px solid #f59e0b", borderRadius: 6, padding: "6px 8px", marginBottom: 8, color: "#92400e" }}>
                    Ese día ya tiene: {personElsewhere.map((x) => `${x.client_name}${x.site_name ? ` · ${x.site_name}` : ""} ${x.is_day_off ? "FRANCO" : `${x.time_from}–${x.time_to}`}`).join(" / ")}
                  </div>
                )}
                {!addForm.is_day_off && (
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <div className="field"><label>Hora desde *</label><Time24Select required disabled={addForm.shift_choice !== "manual"} value={addForm.time_from} onChange={(v) => setAddForm({ ...addForm, time_from: v })} testId="schedule-add-time-from" style={addForm.shift_choice !== "manual" ? { background: "var(--invict-bone-2)" } : undefined} /></div>
                    <div className="field"><label>Hora hasta *</label><Time24Select required disabled={addForm.shift_choice !== "manual"} value={addForm.time_to} onChange={(v) => setAddForm({ ...addForm, time_to: v })} testId="schedule-add-time-to" style={addForm.shift_choice !== "manual" ? { background: "var(--invict-bone-2)" } : undefined} /></div>
                  </div>
                )}
                <div className="field"><label>Notas</label><input value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} data-testid="schedule-add-notes" /></div>

                {!addForm.is_day_off && (
                  <>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, cursor: "pointer", margin: "8px 0" }}>
                      <input type="checkbox" checked={addForm.repeat} onChange={(e) => setAddForm({ ...addForm, repeat: e.target.checked, repeat_to: e.target.checked ? addForm.repeat_to : "" })} data-testid="schedule-add-repeat-toggle"/>
                      <Repeat size={13} /> Repetir en varios días
                    </label>
                    {addForm.repeat && (
                      <div style={{ background: "var(--invict-bone-2)", padding: 10, borderRadius: 6, marginBottom: 8 }} data-testid="schedule-repeat-options">
                        <div className="field"><label>Repetir hasta (inclusive)</label><input required type="date" min={dayModal} value={addForm.repeat_to} onChange={(e) => setAddForm({ ...addForm, repeat_to: e.target.value })} data-testid="schedule-repeat-to"/></div>
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                          {WEEKDAYS.map((w, wd) => (
                            <button type="button" key={wd} onClick={() => toggleWeekday(wd)} data-testid={`schedule-repeat-weekday-${wd}`}
                              style={{ padding: "5px 9px", fontSize: 10, fontWeight: 800, borderRadius: 4, border: "1px solid var(--invict-bone-3)", cursor: "pointer", background: addForm.weekdays.includes(wd) ? "#0A0A0A" : "#fff", color: addForm.weekdays.includes(wd) ? "#FFFBF4" : "#0A0A0A" }}>
                              {w}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                <div style={{ display: "flex", gap: 8 }}>
                  <button type="submit" disabled={busy} className="btn-primary" style={{ flex: 1, padding: 10 }} data-testid="schedule-add-submit">{busy ? "Guardando..." : addForm.is_day_off ? "Asignar franco" : "Asignar turno"}</button>
                  <button type="button" onClick={() => setAddForm(null)} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "0 14px", cursor: "pointer" }} data-testid="schedule-add-cancel">Cancelar</button>
                </div>
              </form>
            ) : (
              !editing && <button onClick={startAdd} className="btn-primary" style={{ width: "100%", padding: 10 }} data-testid="schedule-add-open"><Plus size={14} style={{ display: "inline", marginRight: 6 }} />Agregar turno</button>
            )}
          </div>
        </div>
      )}

      {bulkForm && (
        <div style={overlayStyle} onClick={() => setBulkForm(null)} data-testid="schedule-bulk-modal-overlay">
          <div style={modalStyle} onClick={(e) => e.stopPropagation()} data-testid="schedule-bulk-modal">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#0A0A0A" }}>Asignar a {selectedDays.length} día(s)</div>
              <button onClick={() => setBulkForm(null)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0A0A0A" }}><X size={18} /></button>
            </div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 10 }}>{selectedDays.slice().sort().join(", ")}</div>
            <form onSubmit={submitBulkAssign} style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <div className="field"><label>Objetivo *</label>
                <select required value={bulkForm.client_id} onChange={(e) => setBulkForm({ ...bulkForm, client_id: e.target.value, site_id: "", shift_choice: "manual" })} data-testid="schedule-bulk-client">
                  <option value="">Elegir...</option>
                  {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <SiteSelect clientId={bulkForm.client_id} value={bulkForm.site_id} onChange={(v) => setBulkForm({ ...bulkForm, site_id: v, shift_choice: "manual" })} testIdPrefix="schedule-bulk"/>
              <SiteAssignedHint clientId={bulkForm.client_id} siteId={bulkForm.site_id} roster={roster}/>
              <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                <input type="checkbox" checked={bulkForm.is_day_off} onChange={(e) => setBulkForm({ ...bulkForm, is_day_off: e.target.checked })} data-testid="schedule-bulk-dayoff-toggle"/>
                Franco (sin horario, no suma horas)
              </label>
              {bulkForm.is_day_off && (
                <DayOffKind value={bulkForm.day_off_half} onChange={(v) => setBulkForm({ ...bulkForm, day_off_half: v })} testId="schedule-bulk-dayoff-half" />
              )}
              {!bulkForm.is_day_off && (
                <ShiftSelect clientId={bulkForm.client_id} siteId={bulkForm.site_id} clients={clients} value={bulkForm.shift_choice}
                  onSelectShift={(s) => setBulkForm((f) => (s
                    ? { ...f, shift_choice: s.id, time_from: s.time_from, time_to: s.time_to }
                    : { ...f, shift_choice: "manual" }))}
                  testIdPrefix="schedule-bulk"/>
              )}
              <div className="field"><label>Persona *</label>
                <select required value={bulkForm.user_id} onChange={(e) => setBulkForm({ ...bulkForm, user_id: e.target.value })} data-testid="schedule-bulk-user">
                  <option value="">Elegir...</option>
                  {roster.map((u) => <option key={u.id} value={u.id}>{u.name} · {ROLE_LABELS[u.role] || u.role}</option>)}
                </select>
              </div>
              {!bulkForm.is_day_off && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div className="field"><label>Hora desde *</label><Time24Select required disabled={bulkForm.shift_choice !== "manual"} value={bulkForm.time_from} onChange={(v) => setBulkForm({ ...bulkForm, time_from: v })} testId="schedule-bulk-time-from" style={bulkForm.shift_choice !== "manual" ? { background: "var(--invict-bone-2)" } : undefined} /></div>
                  <div className="field"><label>Hora hasta *</label><Time24Select required disabled={bulkForm.shift_choice !== "manual"} value={bulkForm.time_to} onChange={(v) => setBulkForm({ ...bulkForm, time_to: v })} testId="schedule-bulk-time-to" style={bulkForm.shift_choice !== "manual" ? { background: "var(--invict-bone-2)" } : undefined} /></div>
                </div>
              )}
              <div className="field"><label>Notas</label><input value={bulkForm.notes} onChange={(e) => setBulkForm({ ...bulkForm, notes: e.target.value })} data-testid="schedule-bulk-notes" /></div>
              <div style={{ display: "flex", gap: 8 }}>
                <button type="submit" disabled={busy} className="btn-primary" style={{ flex: 1, padding: 10 }} data-testid="schedule-bulk-submit">{busy ? "Guardando..." : `Asignar a ${selectedDays.length} día(s)`}</button>
                <button type="button" onClick={() => setBulkForm(null)} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "0 14px", cursor: "pointer" }} data-testid="schedule-bulk-cancel">Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {rulesOpen && (
        <ScheduleRulesPanel onClose={() => setRulesOpen(false)} canEdit={isAdmin}
          clientId={filterClient || null}
          clientName={(clients.find((c) => c.id === filterClient) || {}).name}
          roster={roster} />
      )}
      {holidaysOpen && <ScheduleHolidaysPanel onClose={() => setHolidaysOpen(false)} canEdit={isAdmin} year={holidaysYear} onChangeYear={(y) => { setHolidaysYear(y); api.get("/schedule/holidays", { params: { year: y } }).then(() => {}); }} />}

      {repeatOpen && (
        <ScheduleRepeatMonth
          clientId={filterClient}
          siteId={filterSite}
          clientName={(clients.find((c) => c.id === filterClient) || {}).name}
          fromMonth={monthStr}
          onClose={() => setRepeatOpen(false)}
          onDone={(ym) => { setRepeatOpen(false); setYear(Number(ym.slice(0, 4))); setMonth(Number(ym.slice(5, 7))); }}
        />
      )}
      {deletionsOpen && (
        <ScheduleDeletionsPanel
          clients={clients}
          filterClient={filterClient}
          filterSite={filterSite}
          onRestored={load}
          onClose={() => setDeletionsOpen(false)}
        />
      )}

      {/* CAMBIO 4 — Modal de borrado masivo */}
      {bulkDeleteOpen && (
        <div style={overlayStyle} onClick={() => setBulkDeleteOpen(false)} data-testid="schedule-bulk-delete-overlay">
          <div style={{ ...modalStyle, maxWidth: 420 }} onClick={(e) => e.stopPropagation()} data-testid="schedule-bulk-delete-modal">
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 15, fontWeight: 900, color: "#0A0A0A" }}>Eliminar turnos de {selectedDays.length} día(s)</div>
              <button onClick={() => setBulkDeleteOpen(false)} style={{ border: "none", background: "transparent", cursor: "pointer", color: "#0A0A0A" }} data-testid="schedule-bulk-delete-close"><X size={18} /></button>
            </div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)", marginBottom: 12 }}>
              {selectedDays.slice().sort().join(", ")}
            </div>

            <div className="field" style={{ marginBottom: 14 }}>
              <label>Limitar a una persona (opcional)</label>
              <select value={bulkDeleteUserFilter} onChange={(e) => setBulkDeleteUserFilter(e.target.value)} data-testid="schedule-bulk-delete-user">
                <option value="">Todas las personas</option>
                {roster.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>

            <div style={{ background: "rgba(239,68,68,0.08)", border: "1.5px solid #ef4444", borderRadius: 8, padding: "10px 14px", marginBottom: 16 }} data-testid="schedule-bulk-delete-preview">
              <div style={{ fontSize: 13, fontWeight: 800, color: "#b91c1c" }}>
                Vas a borrar <b>{bulkPreviewAssignments.length}</b> turno(s):
              </div>
              {bulkPreviewAssignments.length > 0 && (
                <div style={{ fontSize: 12, color: "#7f1d1d", marginTop: 6 }} data-testid="schedule-bulk-delete-breakdown">
                  {Object.entries(bulkPreviewBySite).map(([siteName, byUser]) => (
                    <div key={siteName} style={{ marginBottom: 4 }}>
                      <b>{siteName}</b> — {Object.entries(byUser).map(([name, n]) => `${name} (${n})`).join(", ")}
                    </div>
                  ))}
                  <div style={{ marginTop: 4 }}>Fechas: {bulkPreviewDatesLabel} de {monthStr}</div>
                </div>
              )}
              {filterSite && (
                <div style={{ fontSize: 11, color: "#4B5563", marginTop: 6, fontStyle: "italic" }} data-testid="schedule-bulk-delete-scope-note">
                  Solo se borra en la sede filtrada ({(filterSites.find((s) => s.id === filterSite) || {}).name}). Las demás sedes de este objetivo no se tocan.
                </div>
              )}
              {bulkPreviewCoverages > 0 && (
                <div style={{ fontSize: 12, color: "#92400e", marginTop: 4 }}>
                  De ellos, <b>{bulkPreviewCoverages}</b> son cobertura(s). El historial de auditoría se conserva.
                </div>
              )}
              {bulkPreviewDayOffs > 0 && (
                <div style={{ fontSize: 12, color: "#4B5563", marginTop: 4 }}>
                  Incluye <b>{bulkPreviewDayOffs}</b> franco(s).
                </div>
              )}
              {bulkPreviewAssignments.length === 0 && (
                <div style={{ fontSize: 12, color: "var(--invict-text-muted)", marginTop: 4 }}>No hay turnos que coincidan con la selección.</div>
              )}
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={submitBulkDelete}
                disabled={bulkDeleteBusy || bulkPreviewAssignments.length === 0}
                className="btn-primary"
                style={{ flex: 1, padding: 10, background: "#ef4444", borderColor: "#ef4444" }}
                data-testid="schedule-bulk-delete-confirm"
              >
                {bulkDeleteBusy ? "Eliminando..." : `Confirmar: borrar ${bulkPreviewAssignments.length} turno(s)`}
              </button>
              <button type="button" onClick={() => setBulkDeleteOpen(false)} style={{ border: "1px solid var(--invict-bone-3)", background: "transparent", borderRadius: 4, padding: "0 14px", cursor: "pointer" }} data-testid="schedule-bulk-delete-cancel">Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
